import { eexRecognitionCellKey } from "./eex-cell-identity.js";
import { z } from "zod";
import { FinanceCarryDetailSchema } from "./eex-finance-carry.js";
const month = z.string().regex(/^(20\d{2}|[3-9]\d{3})-(0[1-9]|1[0-2])$/);
const amount = z.number().finite().nullable();
const optionalText = z.string().nullable();
export const financeExplorerDimensions = ["book", "trader", "pair", "trade"];
export const financeExplorerDimensionLabels = { book: "Account", trader: "Trader", pair: "PAIR", trade: "Trade" };
export const financeExplorerColumns = [
    { key: "openingCarry", label: "Opening carry" }, { key: "calculatedVm", label: "Calculated VM" },
    { key: "currentRecognized", label: "Current movement recognized" }, { key: "currentDeferred", label: "Current movement deferred" },
    { key: "vmToMovement", label: "VM / movement difference" }, { key: "releasedCarry", label: "Earlier carry released" },
    { key: "pnlRounding", label: "P&L rounding" }, { key: "recognizedPnl", label: "Recognized P&L" },
    { key: "transfers", label: "Carry transfers" }, { key: "carryRounding", label: "Carry rounding" },
    { key: "closingCarry", label: "Closing carry" },
];
export const FinanceExplorerSourceValuesSchema = z.object({
    openingCarry: amount, calculatedVm: amount, currentRecognized: amount, currentDeferred: amount,
    releasedCarry: amount, recognizedPnl: amount, transfers: amount, closingCarry: amount,
});
export const FinanceExplorerValuesSchema = FinanceExplorerSourceValuesSchema.extend({ vmToMovement: amount, pnlRounding: amount, carryRounding: amount });
export const FinanceExplorerSortSchema = z.object({
    field: z.enum(["label", "date", ...financeExplorerColumns.map(column => column.key)]),
    direction: z.enum(["asc", "desc"]),
    absolute: z.boolean().optional(),
}).strict();
const FinanceExplorerTradeIdentitySchema = z.object({ product: optionalText, deliveryPeriod: optionalText, tradeDate: optionalText });
export const FinanceExplorerItemSchema = z.object({
    id: z.string(), currency: z.string(), book: optionalText, trader: optionalText, pair: optionalText,
    tradeId: optionalText, tradeLabel: z.string(), relatedTradeIds: z.array(z.string()), contract: optionalText,
    identity: FinanceExplorerTradeIdentitySchema.extend({
        relatedTrades: z.array(FinanceExplorerTradeIdentitySchema.extend({ tradeId: z.string() })),
    }).optional(),
    kind: z.string(), description: z.string(), transferId: optionalText,
    values: FinanceExplorerSourceValuesSchema,
    economics: z.object({
        quantityRole: z.enum(["opening_position", "closing_position", "recognition", "movement", "timing", "transfer"]).optional(),
        eventId: optionalText.optional(), recognitionKind: z.enum(["close", "delivery"]).nullable().optional(),
        side: z.enum(["B", "S"]).nullable().optional(),
        legs: z.array(z.object({ role: z.enum(["inventory", "entry", "exit"]), tradeId: optionalText,
            contract: z.string(), side: z.enum(["B", "S"]).nullable(), price: amount, date: optionalText,
            volume: z.number().finite(), source: z.string() })).optional(),
        quantity: amount, unit: optionalText, priceUnit: optionalText,
        entryPrice: amount, exitPrice: amount, openingMark: amount, closingMark: amount,
        entryDate: optionalText, exitDate: optionalText, sourceMonth: month.nullable(), recognitionMonth: month.nullable(),
        scheduleId: optionalText, reclassId: optionalText, reason: optionalText,
    }),
});
export const FinanceMonthExplorerSchema = z.object({
    schemaVersion: z.literal(1), companyId: z.string(), companyName: z.string(), month,
    generatedAt: z.string(), asOf: z.string(), inputVersion: z.string().regex(/^[a-f0-9]{64}$/),
    currencies: z.array(z.object({ currency: z.string(), values: FinanceExplorerValuesSchema,
        statementVm: amount, statementDifference: amount, unreconciledVm: amount,
        checks: z.array(z.object({ name: z.string(), status: z.enum(["pass", "review"]), detail: z.string() })),
    })),
    approval: z.object({ status: z.enum(["approved", "incomplete", "none"]), approvedEur: amount, currentEur: amount }),
    items: z.array(FinanceExplorerItemSchema), notes: z.array(z.string()),
    companyDetails: z.array(FinanceCarryDetailSchema.extend({ currency: z.string(),
        statementId: optionalText.optional(), reportDate: optionalText.optional(), paymentDate: optionalText.optional(), filename: optionalText.optional(),
    })).optional(),
});
const selection = z.array(optionalText).max(500).optional();
export const FinanceExplorerFiltersSchema = z.object({ book: selection, trader: selection, pair: selection, trade: selection, contract: selection }).strict();
export const FinanceExplorerOptionsSchema = z.object({
    filters: FinanceExplorerFiltersSchema.default({}),
    groupBy: z.array(z.enum(financeExplorerDimensions)).max(4).refine(values => new Set(values).size === values.length).default(["book", "trader", "pair", "trade"]),
    sort: FinanceExplorerSortSchema.optional(),
    search: z.string().trim().max(200).optional(),
}).strict();
export const FinanceExplorerQuerySchema = z.object({ month }).strict();
export const FinanceExplorerExportQuerySchema = z.object({ month, version: z.string().regex(/^[a-f0-9]{64}$/),
    filters: z.string().max(100000).optional(), groupBy: z.string().max(150).optional(), sort: z.string().max(150).optional(), search: z.string().max(200).optional() }).strict();
export function parseFinanceExplorerOptions(query) {
    return FinanceExplorerOptionsSchema.parse({ filters: query.filters === undefined ? {} : JSON.parse(query.filters),
        groupBy: query.groupBy === undefined ? undefined : JSON.parse(query.groupBy),
        ...(query.sort === undefined ? {} : { sort: JSON.parse(query.sort) }),
        ...(query.search === undefined ? {} : { search: query.search }) });
}
export function buildFinanceExplorerExportQuery(monthValue, version, options) {
    const parsed = FinanceExplorerOptionsSchema.parse(options);
    return new URLSearchParams({ month: monthValue, version, filters: JSON.stringify(parsed.filters), groupBy: JSON.stringify(parsed.groupBy),
        ...(parsed.sort === undefined ? {} : { sort: JSON.stringify(parsed.sort) }),
        ...(parsed.search ? { search: parsed.search } : {}) }).toString();
}
const baseKeys = Object.keys(FinanceExplorerSourceValuesSchema.shape);
const keys = financeExplorerColumns.map(column => column.key);
const round = (value) => Number((Math.sign(value) * Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100).toFixed(2));
const sum = (values) => values.some(value => value == null) ? null : round(values.reduce((n, v) => n + v, 0));
const zero = () => Object.fromEntries(keys.map(key => [key, 0]));
const dimensionValue = (item, dimension) => dimension === "trade" ? item.tradeId : item[dimension];
function derived(values) {
    const difference = (parts, signs) => parts.some(value => value == null) ? null : round(parts.reduce((n, value, index) => n + value * signs[index], 0));
    return { ...values,
        vmToMovement: difference([values.calculatedVm, values.currentRecognized, values.currentDeferred], [1, -1, -1]),
        pnlRounding: difference([values.recognizedPnl, values.currentRecognized, values.releasedCarry], [1, -1, -1]),
        carryRounding: difference([values.closingCarry, values.openingCarry, values.currentDeferred, values.releasedCarry, values.transfers], [1, -1, -1, 1, -1]),
    };
}
/** Assign cents once before filtering/grouping. Changing a hierarchy cannot
 * change the displayed value of an item or manufacture a new rounding total. */
function activityDate(dates) {
    const valid = dates.flatMap(value => {
        if (value == null || !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value))
            return [];
        const day = value.slice(0, 10);
        const parsed = new Date(`${day}T00:00:00Z`);
        return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? [day] : [];
    });
    return valid.sort().at(-1) ?? null;
}
function displayedItems(report) {
    const output = report.items.map(item => ({ ...item, displayedValues: derived(item.values),
        activityDate: activityDate([item.economics.entryDate, item.economics.exitDate]) }));
    for (const currency of report.currencies) {
        const items = output.filter(item => item.currency === currency.currency);
        for (const field of baseKeys) {
            const target = currency.values[field];
            const known = items.filter(item => item.values[field] !== null);
            for (const item of known)
                item.displayedValues[field] = round(item.values[field]);
            if (target == null || known.length !== items.length)
                continue;
            let partitions = [{ items: known, target }];
            if (field === "recognizedPnl") {
                // Recognition owns the cent-rounding point: month × book × trader.
                // Global allocation could give a cent to another cell and make a
                // filtered Finance view disagree with the existing monthly P&L cell.
                // Keep aggregateCells' null/empty identity and commercial epsilon.
                const cells = new Map();
                for (const item of known) {
                    const key = eexRecognitionCellKey({ month: report.month, book: item.book, trader: item.trader });
                    const cell = cells.get(key) ?? [];
                    cell.push(item);
                    cells.set(key, cell);
                }
                partitions = [...cells.values()].map(cell => {
                    const native = cell.reduce((n, item) => n + item.values.recognizedPnl, 0);
                    return { items: cell, target: Math.sign(native) * Math.round(Math.abs(native) * 100 + 1e-7) / 100 };
                });
                if (Math.abs(partitions.reduce((n, cell) => n + cell.target, 0) - target) > 1e-7) {
                    throw new Error("Explorer recognized P&L does not match its canonical book/trader cells");
                }
            }
            for (const partition of partitions) {
                const parts = partition.items.filter(item => item.values[field] !== 0).map(item => {
                    const cents = Math.trunc(item.values[field] * 100);
                    return { item, cents, remainder: item.values[field] * 100 - cents };
                });
                const wanted = Math.round(partition.target * 100);
                let remaining = wanted - parts.reduce((n, part) => n + part.cents, 0);
                if (!parts.length) {
                    if (wanted !== 0)
                        throw new Error("Explorer source detail does not support its total");
                    continue;
                }
                // Only display-rounding differences are admissible here. Real source
                // gaps require an explicit unallocated source row from the backend.
                const sourceTotal = partition.items.reduce((n, item) => n + item.values[field], 0);
                if (Math.abs(partition.target - sourceTotal) > (parts.length + 1) * 0.005 + 1e-7)
                    throw new Error("Explorer source detail has an unexplained difference");
                parts.sort((a, b) => (remaining >= 0 ? b.remainder - a.remainder : a.remainder - b.remainder) || a.item.id.localeCompare(b.item.id));
                const whole = Math.trunc(remaining / parts.length);
                for (const part of parts)
                    part.cents += whole;
                remaining -= whole * parts.length;
                for (let i = 0; i < Math.abs(remaining); i++)
                    parts[i].cents += Math.sign(remaining);
                for (const part of parts)
                    part.item.displayedValues[field] = part.cents / 100;
            }
        }
        for (const item of items)
            item.displayedValues = derived(item.displayedValues);
    }
    return output;
}
export function projectFinanceMonthExplorer(report, selected = {}) {
    const options = FinanceExplorerOptionsSchema.parse(selected);
    const sort = options.sort ?? { field: "label", direction: "asc" };
    const compare = (a, b) => {
        const av = sort.field === "label" ? a.label : sort.field === "date" ? a.activityDate : a.values[sort.field];
        const bv = sort.field === "label" ? b.label : sort.field === "date" ? b.activityDate : b.values[sort.field];
        // Incomplete dates or amounts are always last, even for descending sorts.
        if (av == null && bv != null)
            return 1;
        if (av != null && bv == null)
            return -1;
        const ordered = av == null || bv == null ? 0 : typeof av === "number" && typeof bv === "number"
            ? (sort.absolute ? Math.abs(av) - Math.abs(bv) : av - bv) : String(av).localeCompare(String(bv));
        return ordered * (sort.direction === "desc" ? -1 : 1) || a.label.localeCompare(b.label) || a.id.localeCompare(b.id);
    };
    const displayed = displayedItems(report);
    const filterOptions = Object.fromEntries([...financeExplorerDimensions, "contract"].map(dimension => {
        const values = new Map();
        for (const item of displayed) {
            const value = dimensionValue(item, dimension);
            values.set(JSON.stringify(value), { value, label: value == null ? (dimension === "pair" ? "Unpaired" : "Unassigned") : dimension === "trade" ? item.tradeLabel : value });
        }
        return [dimension, [...values.values()].sort((a, b) => a.label.localeCompare(b.label))];
    }));
    const normalize = (value) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("en");
    const searchTerms = normalize(options.search ?? "").split(/\s+/).filter(Boolean);
    const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
    const readableDate = (value) => {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value))
            return null;
        const parsed = new Date(`${value}T00:00:00Z`);
        return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? dateFormatter.format(parsed) : null;
    };
    const matchesSearch = (item) => {
        if (!searchTerms.length)
            return true;
        const identity = item.identity;
        const text = normalize([item.tradeLabel, item.tradeId, ...item.relatedTradeIds, item.contract, item.pair, item.book, item.trader,
            item.description, identity?.product, identity?.deliveryPeriod, identity?.tradeDate, readableDate(identity?.tradeDate),
            ...(identity?.relatedTrades.flatMap(trade => [trade.tradeId, trade.product, trade.deliveryPeriod, trade.tradeDate, readableDate(trade.tradeDate)]) ?? [])]
            .filter(value => value != null).join(" "));
        return searchTerms.every(term => text.includes(term));
    };
    const items = displayed.filter(item => matchesSearch(item) && Object.entries(options.filters).every(([dimension, values]) => !values?.length || values.includes(dimensionValue(item, dimension))))
        .map(item => ({ item, id: item.id, label: item.identity ? item.tradeLabel : item.description,
        activityDate: item.activityDate, values: item.displayedValues }))
        .sort(compare).map(entry => entry.item);
    const totals = (sources) => sources.length === 0 ? zero() : Object.fromEntries(keys.map(key => [key, sum(sources.map(item => item.displayedValues[key]))]));
    const tree = (sources, level, prefix) => {
        const dimension = options.groupBy[level];
        if (!dimension)
            return [];
        const groups = new Map();
        for (const item of sources) {
            const key = JSON.stringify(dimensionValue(item, dimension));
            const group = groups.get(key) ?? [];
            group.push(item);
            groups.set(key, group);
        }
        return [...groups.entries()].map(([key, grouped]) => {
            const value = JSON.parse(key);
            const id = `${prefix}/${dimension}:${key}`;
            return { id, label: value == null ? (dimension === "pair" ? "Unpaired" : "Unassigned") : dimension === "trade" ? grouped[0].tradeLabel : value,
                dimension, value, level, activityDate: activityDate(grouped.map(item => item.activityDate)),
                values: totals(grouped), leafIds: grouped.map(item => item.id), children: tree(grouped, level + 1, id) };
        }).sort(compare);
    };
    const currencies = report.currencies.map(currency => {
        const sources = items.filter(item => item.currency === currency.currency);
        return { currency: currency.currency, values: totals(sources),
            nodes: options.groupBy.length ? tree(sources, 0, currency.currency) : [{ id: `${currency.currency}/all`, label: "Selected sources", dimension: null, value: null, level: 0,
                    activityDate: activityDate(sources.map(item => item.activityDate)), values: totals(sources), leafIds: sources.map(item => item.id), children: [] }] };
    });
    // Workbook source rows follow the same currency, hierarchy and leaf ordering
    // as the fully expanded screen; ordering never participates in cent allocation.
    const leafOrder = (nodes) => nodes.flatMap(node => node.children.length ? leafOrder(node.children) : node.leafIds);
    const byId = new Map(items.map(item => [item.id, item]));
    return { options, filterOptions, items: currencies.flatMap(currency => leafOrder(currency.nodes).map(id => byId.get(id))), currencies };
}
//# sourceMappingURL=eex-finance-explorer.js.map